import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SIGNATURE_SUBS, BADA_ONLY, SIDES, COMBOS } from "../data/menu";

const Tag = ({ children }) => (
  <span className="ml-2 rounded-full border-2 border-gulabi px-2 py-0.5 align-middle font-bungee text-[10px] text-gulabi">
    {children}
  </span>
);

const Thumb = ({ item, onSelect, border = "border-taxi", size = "h-20 w-20 sm:h-24 sm:w-24" }) => (
  <motion.img
    src={item.img}
    alt={item.name}
    data-testid={`menu-img-${item.name.toLowerCase().replace(/\s/g, "-")}`}
    onClick={() => onSelect(item)}
    whileHover={{ scale: 1.12, rotate: -3 }}
    whileTap={{ scale: 0.95 }}
    transition={{ type: "spring", stiffness: 260, damping: 15 }}
    className={`${size} ${border} shrink-0 cursor-pointer rounded-2xl border-4 object-cover sticker-shadow-sm`}
  />
);

const SubRow = ({ item, i, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.35, delay: i * 0.06 }}
    className="flex items-start gap-4 py-4"
  >
    <Thumb item={item} onSelect={onSelect} />
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-anton text-lg uppercase text-pav">
          {item.name}
          {item.tag && <Tag>{item.tag}</Tag>}
        </h3>
        <span className="mx-1 hidden flex-1 border-b-2 border-dotted border-pav/30 sm:block" />
        <span className="shrink-0 font-bungee text-taxi">
          {item.chota} / {item.bada}
        </span>
      </div>
      <p className="mt-1 max-w-md text-sm text-pav/60">{item.desc}</p>
    </div>
  </motion.div>
);

const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="menu-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-kadak/85 p-4 backdrop-blur-sm"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.85, rotate: -3, y: 40 }}
        animate={{ scale: 1, rotate: 0, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="relative grid max-h-[90vh] w-full max-w-3xl grid-cols-1 overflow-y-auto rounded-3xl border-4 border-kadak bg-pav sticker-shadow-taxi sm:grid-cols-2"
      >
        <div className="relative">
          <img src={item.img} alt={item.name} className="h-60 w-full object-cover sm:h-full sm:min-h-[26rem]" />
          {item.tag && (
            <span className="absolute left-4 top-4 -rotate-3 rounded-full border-2 border-kadak bg-gulabi px-3 py-1 font-bungee text-xs text-kadak">
              {item.tag}
            </span>
          )}
        </div>
        <div className="flex flex-col justify-center p-6 sm:p-8">
          <h3 className="font-shrikhand text-3xl text-mirchi sm:text-4xl">{item.name}</h3>
          <p className="mt-3 text-sm leading-relaxed text-kadak/70">{item.desc}</p>
          <p className="mt-4 border-t-2 border-dashed border-kadak/20 pt-4 font-shrikhand text-lg leading-snug text-chutney">
            {item.story}
          </p>
          <div className="mt-5 flex flex-wrap gap-2 font-bungee text-xs">
            {item.chota ? (
              <>
                <span className="rounded-full border-2 border-kadak bg-pav px-4 py-1.5 text-kadak">CHOTA ₹{item.chota}</span>
                <span className="rounded-full border-2 border-kadak bg-taxi px-4 py-1.5 text-kadak">BADA ₹{item.bada}</span>
              </>
            ) : (
              <span className="rounded-full border-2 border-kadak bg-taxi px-4 py-1.5 text-kadak">₹{item.price}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="menu-lightbox-close"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border-4 border-kadak bg-mirchi font-bungee text-sm text-pav sticker-shadow-sm transition-transform duration-150 hover:rotate-90"
        >
          X
        </button>
      </motion.div>
    </motion.div>
  );
};

export const MenuBoard = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="menu" data-testid="menu-section" className="scroll-mt-16 border-y-4 border-kadak bg-kadak py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="font-bungee text-xs tracking-[0.35em] text-gulabi sm:text-sm">THE MENU</p>
            <h2 className="mt-4 font-shrikhand text-4xl leading-tight text-taxi sm:text-5xl lg:text-6xl">
              Seven subs. Zero decision fatigue.
            </h2>
            <p className="mt-6 max-w-md text-base text-pav/70">
              No 40-item board. One hero product in seven personalities — every sub a Chota for the daily
              run, or a Bada for the big hunger.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="rounded-2xl border-4 border-taxi bg-kadak px-5 py-3 sticker-shadow-taxi">
                <p className="font-bungee text-2xl text-taxi">₹79</p>
                <p className="text-xs text-pav/60">entry price — chai included territory</p>
              </div>
              <div className="rounded-2xl border-4 border-pav/50 bg-kadak px-5 py-3">
                <p className="font-bungee text-2xl text-pav">70%</p>
                <p className="text-xs text-pav/60">of menu from just five base ingredients</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 font-bungee text-xs tracking-widest">
              <span className="rounded-full border-2 border-pav px-4 py-1.5 text-pav">CHOTA • ROZ WALA • ₹79–129</span>
              <span className="rounded-full border-2 border-taxi px-4 py-1.5 text-taxi">BADA • BADA MAZA • ₹139–219</span>
            </div>
            <p className="mt-8 inline-block -rotate-1 rounded-full border-2 border-gulabi px-4 py-1.5 font-bungee text-[10px] tracking-widest text-gulabi">
              TAP ANY PHOTO — FULL PICTURE + STORY
            </p>
          </div>

          <div className="overflow-hidden rounded-3xl border-4 border-taxi bg-[#2B2114]">
            <div className="tape-checker h-6 w-full" />
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-dashed border-taxi/50 pb-4">
                <h3 className="font-shrikhand text-3xl text-taxi">The Menu</h3>
                <p className="font-bungee text-[10px] tracking-[0.25em] text-pav sm:text-xs">
                  100% VEG • FRIED FRESH • NO SHORTCUTS
                </p>
              </div>
              <div className="mt-2 flex justify-end gap-6 pt-3 font-bungee text-[10px] tracking-widest text-taxi">
                <span>CHOTA</span>
                <span>BADA</span>
              </div>
              <p className="mt-4 font-bungee text-xs tracking-[0.3em] text-gulabi">SIGNATURE SUBS</p>
              <div className="divide-y divide-pav/10">
                {SIGNATURE_SUBS.map((s, i) => (
                  <SubRow key={s.name} item={s} i={i} onSelect={setSelected} />
                ))}
              </div>
              <p className="mt-6 font-bungee text-xs tracking-[0.3em] text-gulabi">BADA ONLY</p>
              <div className="flex items-start gap-4 py-4">
                <Thumb item={BADA_ONLY} onSelect={setSelected} border="border-gulabi" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-anton text-lg uppercase text-pav">
                      {BADA_ONLY.name}
                      <Tag>{BADA_ONLY.tag}</Tag>
                    </h3>
                    <span className="mx-1 hidden flex-1 border-b-2 border-dotted border-pav/30 sm:block" />
                    <span className="shrink-0 font-bungee text-taxi">{BADA_ONLY.price}</span>
                  </div>
                  <p className="mt-1 max-w-md text-sm text-pav/60">{BADA_ONLY.desc}</p>
                </div>
              </div>
              <p className="mt-4 font-bungee text-xs tracking-[0.3em] text-gulabi">SIDES</p>
              <div className="mt-2 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                {SIDES.map((s) => (
                  <div key={s.name} className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-3">
                      <Thumb item={s} onSelect={setSelected} size="h-14 w-14" />
                      <div>
                        <h4 className="font-anton text-sm uppercase text-pav">{s.name}</h4>
                        <p className="text-xs text-pav/50">{s.desc}</p>
                      </div>
                    </div>
                    <span className="shrink-0 font-bungee text-sm text-taxi">{s.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 font-bungee text-xs tracking-[0.3em] text-gulabi">COMBOS</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {COMBOS.map((c) => (
                  <div key={c.name} className="rounded-xl border-2 border-chutney bg-chutney/20 p-4">
                    <div className="flex items-baseline justify-between">
                      <h4 className="font-bungee text-sm text-pav">{c.name}</h4>
                      <span className="font-bungee text-taxi">₹{c.price}</span>
                    </div>
                    <p className="mt-1 text-xs text-pav/60">{c.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-center font-bungee text-[10px] tracking-[0.3em] text-pav/50">
                SAB PRICES IN ₹ • HORN OK PAV • VADASUB.IN
              </p>
            </div>
            <div className="tape-checker h-6 w-full" />
          </div>
        </div>
      </div>
      <AnimatePresence>{selected && <Lightbox item={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
    </section>
  );
};
